import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { apiUrl } from "@/lib/api";
import { motion } from "framer-motion";
import { Send } from "lucide-react";
import { gaTrack, metaTrack } from "@/lib/analytics";
import SEO from "../components/SEO";
import GetStartedBar from "../components/GetStartedBar";
import CalendlyEmbed from "../components/CalendlyEmbed";

/* ------------------------------------------------------------------ */
/*  Contact, with a walkthrough mode. The landing pages offer two       */
/*  doors: self-serve (primary) and a 15-minute walkthrough for orgs    */
/*  that won't self-onboard a payments platform. /walkthrough renders   */
/*  this page with the ask reframed, so that second CTA has a real      */
/*  destination instead of dropping people on a generic contact form.   */
/*  Requests are tagged in the message body so they're sortable in the  */
/*  inbox without a backend change.                                     */
/* ------------------------------------------------------------------ */
export default function ContactPage() {
  const { pathname, search } = useLocation();
  const isWalkthrough =
    pathname === "/walkthrough" ||
    new URLSearchParams(search).get("intent") === "walkthrough";

  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Set when the booker can't render (no URL configured, cookie consent
  // declined, or the script was blocked) so the form takes the page back.
  const [bookerHidden, setBookerHidden] = useState(false);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const payload = isWalkthrough
        ? { ...formData, message: `[Walkthrough request]\n\n${formData.message}` }
        : formData;
      const res = await fetch(apiUrl("/api/contact/submit/"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({})); // Handle non-JSON responses

      if (!res.ok) {
        throw new Error(data?.error || "Failed to send message.");
      }

      // A walkthrough request is a sales lead, not a support note. Fire the
      // conversion events or the second door stays invisible in GA and Meta.
      if (isWalkthrough) {
        gaTrack("generate_lead", { location: "walkthrough_form", account_type: "organization" });
        metaTrack("Lead");
      }

      setFormData({ name: "", email: "", message: "" });
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      console.error("Error submitting message:", err);
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full px-4 py-3 rounded-xl bg-bg text-ink border border-line-control focus:border-brand focus:ring-2 focus:ring-brand/20 focus:outline-none transition-all placeholder:text-faint";

  return (
    <div className="bg-bg text-ink flex items-center justify-center px-4 py-16">
      <SEO
        title={isWalkthrough ? "Book a walkthrough" : "Contact"}
        description={
          isWalkthrough
            ? "Book a 15-minute walkthrough of Sift Careers. See how hiring, contracts, scheduling, timecards, and payment run in one place."
            : "Get in touch with Sift Careers. Ask questions, share feedback, or learn how our contractor management platform can help your business."
        }
        path={isWalkthrough ? "/walkthrough" : "/contact"}
        keywords={["contact sift careers", "contractor management demo", "staffing platform demo"]}
      />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-2xl"
      >
        <div className="text-center mb-9">
          <div className="font-mono uppercase text-[0.65625rem] tracking-[.24em] text-brand mb-3">
            {isWalkthrough ? "Walkthrough" : "Contact"}
          </div>
          <h1 className="font-sans font-bold tracking-tight text-4xl sm:text-5xl text-ink">
            {isWalkthrough ? (
              <>See it in <span className="font-display italic font-semibold">15 minutes.</span></>
            ) : (
              <>Get in <span className="font-display italic font-semibold">touch.</span></>
            )}
          </h1>
          <p className="text-secondary text-lg mt-4">
            {isWalkthrough
              ? "Tell us a bit about your team and we'll set up a time. No deck, no pressure, just the product."
              : "Have questions or feedback? We'd love to hear from you."}
          </p>
        </div>

        {/* Booking beats a form here: the CTA says "book a 15-minute
            walkthrough", so the page should let them pick a time. The form
            stays underneath for people who would rather ask a question first,
            and becomes the whole page again if Calendly is unset, blocked by an
            ad blocker, or declined via the cookie banner. */}
        {isWalkthrough && !bookerHidden && (
          <div className="mb-8">
            <CalendlyEmbed onUnavailable={() => setBookerHidden(true)} />
          </div>
        )}

        {isWalkthrough && !bookerHidden && (
          <p className="text-center text-sm text-secondary mb-6">
            Rather ask a question first? Send a note instead.
          </p>
        )}

        <form onSubmit={handleSubmit} className="space-y-6 bg-surface p-6 sm:p-8 rounded-2xl border border-line shadow-sift-md">
          {error && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center text-red-700 bg-red-50 border border-red-200 rounded-xl p-4"
            >
              ✗ {error}
            </motion.div>
          )}

          <div>
            <label htmlFor="contact-page-name" className="block text-sm font-semibold mb-2 text-secondary">Name</label>
            <input id="contact-page-name"
              type="text"
              name="name"
              className={inputClass}
              placeholder="Your name"
              value={formData.name}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>

          <div>
            <label htmlFor="contact-page-email" className="block text-sm font-semibold mb-2 text-secondary">Email</label>
            <input id="contact-page-email"
              type="email"
              name="email"
              className={inputClass}
              placeholder="you@example.com"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>

          {/* Optional on purpose. Requiring a paragraph before we'd take an
              email is the likeliest reason this form had twelve visits and zero
              submissions. Short rows so it reads as a nicety, not a homework
              box. */}
          <div>
            <label htmlFor="contact-message" className="block text-sm font-semibold mb-2 text-secondary">
              {isWalkthrough ? "Anything we should know?" : "Message"}{" "}
              <span className="font-normal text-tertiary">(optional)</span>
            </label>
            <textarea
              id="contact-message"
              name="message"
              rows={isWalkthrough ? 3 : 6}
              className={`${inputClass} resize-none`}
              placeholder={
                isWalkthrough
                  ? "Roughly how many people you engage, or what you're hoping to solve."
                  : "Tell us what's on your mind..."
              }
              value={formData.message}
              onChange={handleChange}
              disabled={loading}
            />
          </div>

          <div className="flex justify-center pt-2">
            <button
              type="submit"
              disabled={loading}
              className="bg-brand hover:bg-brand-hover text-white font-semibold px-8 py-3 rounded-pill shadow-sift-md transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Send size={18} />
              {loading ? "Sending..." : isWalkthrough ? "Request a walkthrough" : "Send Message"}
            </button>
          </div>

          {submitted && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center text-brand bg-brand-tint border border-line rounded-xl p-4"
            >
              ✓ {isWalkthrough ? "Got it. We'll reach out to schedule your walkthrough." : "Message sent successfully! We'll get back to you soon."}
            </motion.div>
          )}
        </form>
      </motion.div>

      <GetStartedBar message="Prefer to just see it in action?" sub="Free to join. Set up in under two minutes." location="contact_sticky_bar" />
    </div>
  );
}
